import React, { useEffect, useState } from "react";
import { Calculator, ChevronDown, ChevronRight } from "lucide-react";
import { estimateCost, fmtUsd } from "../../services/costEstimate";
import { getBundleV2, listAllVersions, type FlatVersion } from "../../api/workbench";
import type { CheckVersion, CorpusSet, CostEstimate } from "../../types";

export interface CorpusEstimateBarProps {
  corpus: CorpusSet;
  onEstimated: (corpusId: string, checkBundleId: string, est: CostEstimate) => void;
}

/* Utility strip at the top of the corpus detail view — projects the cost of a
   full simulation run (corpus × bundle version) without spending LLM calls.
   Versions come from the agent-bundles API; the selected version's v2 agents
   are adapted into the estimator's prompt-size/model/out-token shape. */
export function CorpusEstimateBar(props: CorpusEstimateBarProps): JSX.Element {
  const { corpus, onEstimated } = props;
  const [versions, setVersions] = useState<FlatVersion[]>([]);
  const [bundleId, setBundleId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CostEstimate | null>(null);
  const [error, setError] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  useEffect(() => {
    void listAllVersions().then((vs) => {
      setVersions(vs);
      setBundleId((cur) => cur || (vs.some((v) => v.versionId === corpus.lastEstimate?.checkBundleId)
        ? corpus.lastEstimate!.checkBundleId
        : vs[0]?.versionId ?? ""));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (): Promise<void> => {
    if (!bundleId || busy) return;
    setBusy(true);
    setError(false);
    const flat = versions.find((v) => v.versionId === bundleId);
    const detail = flat ? await getBundleV2(flat.bundleId) : null;
    const snapV2 = detail?.versions.find((v) => v.id === bundleId)?.snap;
    const asCheckVersion: CheckVersion | null = snapV2
      ? {
          id: bundleId,
          label: flat!.label,
          when: flat!.createdAt,
          note: "",
          snap: Object.fromEntries(
            Object.entries(snapV2)
              .filter(([, a]) => a.enabled)
              .map(([id, a]) => [
                id,
                {
                  name: a.name, desc: a.desc, prompt: a.prompt, tau: a.tau,
                  model: a.model, outTok: a.maxTokens, external: false,
                  threshold_rationale: "",
                },
              ]),
          ),
        }
      : null;
    const est = asCheckVersion
      ? await estimateCost(corpus.id, bundleId, [corpus], [asCheckVersion])
      : null;
    setBusy(false);
    if (!est) {
      setError(true);
      return;
    }
    setResult(est);
    onEstimated(corpus.id, bundleId, est);
  };

  const modelSplit: Array<[string, number]> = result
    ? Object.entries(
        Object.values(result.perCheck).reduce<Record<string, number>>(
          (acc, pc) => {
            acc[pc.model] = (acc[pc.model] || 0) + pc.cost;
            return acc;
          },
          {},
        ),
      )
    : [];

  return (
    <div
      className="card"
      style={{ padding: "10px 14px", marginBottom: 12, background: "var(--subtle)" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span className="up" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Calculator size={13} /> Cost estimate
        </span>
        <select
          className="sel"
          style={{ width: 220 }}
          value={bundleId}
          onChange={(e) => setBundleId(e.target.value)}
          disabled={busy}
        >
          {versions.map((v) => (
            <option key={v.versionId} value={v.versionId}>
              {v.bundleName} / {v.label} ({v.agentCount} agents)
            </option>
          ))}
        </select>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => void run()}
          disabled={busy || !bundleId || corpus.emails.length === 0}
        >
          {busy ? <span className="spin" /> : <Calculator size={13} />}
          {busy ? "Estimating…" : "Estimate Cost"}
        </button>
        {error && (
          <span className="tert" style={{ fontSize: 12, color: "var(--red, #c0392b)" }}>
            Estimate failed — is the API reachable?
          </span>
        )}
        {result && !busy && (
          <>
            <span className="mono" style={{ fontWeight: 600, fontSize: 14 }}>
              {fmtUsd(result.totalCost)}
            </span>
            <span className="mono tert" style={{ fontSize: 11 }}>
              {result.emailCount} emails × {result.checkCount} checks ·{" "}
              {(result.totalInputTokens / 1000).toFixed(1)}k in /{" "}
              {(result.totalOutputTokens / 1000).toFixed(1)}k out tok
            </span>
            <button
              className="icon-btn"
              title="Per-model split"
              onClick={() => setShowSplit((s) => !s)}
            >
              {showSplit ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          </>
        )}
        {!result && !busy && !error && corpus.lastEstimate && (
          <span className="mono tert" style={{ fontSize: 11 }}>
            last estimate {fmtUsd(corpus.lastEstimate.cost)} · {corpus.lastEstimate.at}
          </span>
        )}
      </div>
      {result && showSplit && (
        <div style={{ marginTop: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
          {modelSplit.map(([model, cost]) => (
            <span className="mono tert" key={model} style={{ fontSize: 11 }}>
              {model} → {fmtUsd(cost)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
